import React from 'react';
import { Box, Typography, Alert, CircularProgress, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { OrganizationExistenceCheckDto } from '../../api/types/dtos/organization.dto';

interface CollisionCheckBannerProps {
  checkResult: OrganizationExistenceCheckDto | null;
  loading: boolean;
  error: string | null;
  duplicateCount: number;
  totalCount: number;
  mode: 'upsert' | 'delete';
  existingAction: 'skip' | 'update';
}

const CollisionCheckBanner: React.FC<CollisionCheckBannerProps> = ({
  checkResult,
  loading,
  error,
  duplicateCount,
  totalCount,
  mode,
  existingAction,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Verificando organizaciones existentes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }}>
        No se pudo verificar organizaciones existentes: {error}
      </Alert>
    );
  }

  if (!checkResult) return null;

  const existingCount = Object.keys(checkResult.existingOrgs).length;
  const newCount = checkResult.notFoundIdNumbers.length;
  const notFoundCount = mode === 'delete' ? newCount : 0;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5, alignItems: 'center' }}>
      {mode === 'upsert' && (
        <>
          {newCount > 0 && (
            <Chip
              icon={<CheckCircleIcon />}
              label={`${newCount} nueva${newCount === 1 ? '' : 's'} organizacion${newCount === 1 ? '' : 'es'} sera${newCount === 1 ? '' : 'n'} creada${newCount === 1 ? '' : 's'}`}
              color="success"
              variant="outlined"
              size="small"
            />
          )}
          {existingCount > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${existingCount} ya existe${existingCount === 1 ? '' : 'n'} (${existingAction === 'skip' ? 'se omitiran' : 'se actualizaran'})`}
              color={existingAction === 'update' ? 'info' : 'warning'}
              variant="outlined"
              size="small"
            />
          )}
        </>
      )}

      {mode === 'delete' && (
        <>
          {existingCount > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${existingCount} organizacion${existingCount === 1 ? '' : 'es'} sera${existingCount === 1 ? '' : 'n'} eliminada${existingCount === 1 ? '' : 's'}`}
              color="error"
              variant="outlined"
              size="small"
            />
          )}
          {notFoundCount > 0 && (
            <Chip
              icon={<WarningIcon />}
              label={`${notFoundCount} no encontrada${notFoundCount === 1 ? '' : 's'} en la base de datos`}
              color="warning"
              variant="outlined"
              size="small"
            />
          )}
        </>
      )}

      {duplicateCount > 0 && (
        <Chip
          icon={<ErrorIcon />}
          label={`${duplicateCount} ID duplicado${duplicateCount > 1 ? 's' : ''} en el CSV`}
          color="error"
          size="small"
        />
      )}

      {totalCount > 0 && existingCount === 0 && duplicateCount === 0 && newCount === 0 && (
        <Typography variant="body2" color="textSecondary">
          No se detectaron problemas para {totalCount} registro{totalCount === 1 ? '' : 's'}.
        </Typography>
      )}
    </Box>
  );
};

export default CollisionCheckBanner;
